<script setup lang="ts">
import {
  PhCheck,
  PhFlagCheckered,
  PhHammer,
  PhPaperPlaneTilt,
  PhReceipt,
  PhSpinnerGap,
  PhX,
} from '@phosphor-icons/vue';
import { onClickOutside } from '@vueuse/core';
import { models } from 'cesg-shared';
import { ref, type Component } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps<{
  modelValue: models.domain.AtaStatusType | null | undefined;
  availableStatuses: models.domain.AtaStatusType[];
  disabled?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: models.domain.AtaStatusType];
}>();

const { t } = useI18n();

const open = ref(false);
const container = ref<HTMLElement | null>(null);
onClickOutside(container, () => (open.value = false));

const statusIcon: Record<models.domain.AtaStatusType, Component> = {
  [models.domain.AtaStatusType.UnderDevelopment]: PhHammer,
  [models.domain.AtaStatusType.OnReferral]: PhPaperPlaneTilt,
  [models.domain.AtaStatusType.Approved]: PhCheck,
  [models.domain.AtaStatusType.Rejected]: PhX,
  [models.domain.AtaStatusType.InProgress]: PhSpinnerGap,
  [models.domain.AtaStatusType.Done]: PhFlagCheckered,
  [models.domain.AtaStatusType.InvoicingOk]: PhReceipt,
};

function select(status: models.domain.AtaStatusType) {
  emit('update:modelValue', status);
  open.value = false;
}
</script>

<template>
  <div
    ref="container"
    class="status-select"
    :class="{ 'is-disabled': disabled }"
  >
    <button
      type="button"
      class="status-select__trigger"
      :disabled="disabled"
      @click="open = !open"
    >
      <span v-if="modelValue" class="status-icon-circle" :class="modelValue">
        <component :is="statusIcon[modelValue]" :size="14" weight="regular" />
      </span>
      <span>{{ modelValue ? t(modelValue) : '' }}</span>
    </button>

    <div v-if="open" class="status-select__options">
      <button
        v-for="status in availableStatuses"
        :key="status"
        type="button"
        class="status-select__option"
        :class="{ 'is-selected': modelValue === status }"
        @click="select(status)"
      >
        <span class="status-icon-circle" :class="status">
          <component :is="statusIcon[status]" :size="14" weight="regular" />
        </span>
        <span>{{ t(status) }}</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/style/variables';

.status-select {
  position: relative;
  display: block;
  width: 100%;

  &.is-disabled .status-select__trigger {
    cursor: default;
    opacity: 0.6;
    pointer-events: none;
  }
}

.status-select__trigger {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: calc(0.5em - 1px) 2.25em calc(0.5em - 1px) 0.45em;
  height: 2.5em;
  width: 100%;
  background-color: white;
  border: 1px solid #dbdbdb;
  border-radius: 9999px;
  font-size: 1rem;
  cursor: pointer;

  // Mimic Bulma's select arrow
  &::after {
    content: '';
    position: absolute;
    right: 0.75em;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
    width: 0.5em;
    height: 0.5em;
    border: 2px solid #3273dc;
    border-top: none;
    border-left: none;
    pointer-events: none;
  }

  &:hover {
    border-color: #b5b5b5;
  }
}

.status-select__options {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  z-index: 20;
  width: 100%;
  background: white;
  border: 1px solid #dbdbdb;
  border-radius: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.status-select__option {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: calc(0.5em - 1px) calc(0.625em - 1px);
  font-size: 1rem;
  cursor: pointer;
  border: none;
  background: white;
  text-align: left;

  &:hover {
    background-color: #f5f5f5;
  }

  &.is-selected {
    font-weight: 600;
  }
}

.status-icon-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6em;
  height: 1.6em;
  border-radius: 50%;
  flex-shrink: 0;

  &.underdevelopment {
    background-color: var(--ata-underdevelopment);
  }
  &.onreferral {
    background-color: var(--ata-onreferral);
  }
  &.approved {
    background-color: var(--ata-approved);
  }
  &.rejected {
    background-color: var(--ata-rejected);
  }
  &.inprogress {
    background-color: var(--ata-inprogress);
  }
  &.done {
    background-color: var(--ata-done);
  }
  &.invoicingok {
    background-color: var(--ata-invoicingok);
  }
}
</style>
